import React, { createContext, useEffect, useReducer, useState } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import jwtDecode from "jwt-decode";
import { actions, useAppState } from "./AppStateContext";
import { useLogoutMutation, useValidateSessionQuery } from "../graphql/generated/graphql";
import { usePostHog } from "posthog-js/react";
import Sentry from "../utils/sentry";
import returnToUtils from "../utils/returnTo.utils";

interface DecodedToken {
  userId: string;
  email: string;
  name: string;
  deviceId: string;
  isEmailVerified: boolean;
  otpVerifiedForSession: boolean;
  iat: number;
  exp: number;
}

interface AuthContextType {
  isUpdating: boolean;
  isAuthenticated: boolean;
  isEmailVerified: boolean;
  otpVerifiedForSession: boolean;
  login: (accessToken: string, refreshToken: string) => Promise<void>;
  logout: () => Promise<void>;
  verifyOTPForSession: (accessToken: string, refreshToken: string) => Promise<void>;
}

export const decodeToken = async (token: string) => {
  const decoded = jwtDecode<DecodedToken>(token);
  return decoded;
};

export const AuthContext = createContext<AuthContextType>({
  isUpdating: false,
  isAuthenticated: false,
  isEmailVerified: false,
  otpVerifiedForSession: false,
  login: async () => {},
  logout: async () => {},
  verifyOTPForSession: async () => {},
});

const authReducer = (state, action) => {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    case "SET_AUTH_STATE":
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

export const AuthProvider = ({ children, apolloClient }) => {
  const [isUpdating, setIsUpdating] = useState(true);
  const [state, dispatch] = useReducer(authReducer, {
    isLoading: true,
    isAuthenticated: false,
    isEmailVerified: false,
    otpVerifiedForSession: false,
  });

  const { dispatch: appDispatch } = useAppState();
  const posthog = usePostHog();

  const { data, loading, startPolling, stopPolling } = useValidateSessionQuery({
    notifyOnNetworkStatusChange: false,
    fetchPolicy: "network-only",
    skip: !state.isAuthenticated,
  });

  const [logoutMutation] = useLogoutMutation();

  useEffect(() => {
    const checkToken = async () => {
      const accessToken = await AsyncStorage.getItem("accessToken");
      const refreshToken = await AsyncStorage.getItem("refreshToken");

      if (accessToken && refreshToken) {
        const decodedAccessToken = await decodeToken(accessToken);

        posthog?.identify(decodedAccessToken.userId);
        dispatch({
          type: "SET_AUTH_STATE",
          payload: {
            isLoading: false,
            isAuthenticated: true,
            isEmailVerified: decodedAccessToken.isEmailVerified,
            otpVerifiedForSession: decodedAccessToken.otpVerifiedForSession,
          },
        });
      } else {
        posthog?.reset(true);
        dispatch({
          type: "SET_AUTH_STATE",
          payload: {
            isLoading: false,
            isAuthenticated: false,
            isEmailVerified: false,
            otpVerifiedForSession: false,
          },
        });
        setIsUpdating(false);
      }
    };

    if (isUpdating) {
      checkToken();
    }
    setIsUpdating(false);
  }, [isUpdating]);

  useEffect(() => {
    const checkSession = async () => {
      if (data && !data.validateSession.isValid) {
        posthog?.reset(true);
      }
    };

    checkSession();
  }, [data]);

  useEffect(() => {
    const pollInterval = 60000;
    startPolling(pollInterval);

    return () => {
      stopPolling();
    };
  }, []);

  const login = async (accessToken, refreshToken) => {
    setIsUpdating(true);

    try {
      await AsyncStorage.setItem("accessToken", accessToken);
      await AsyncStorage.setItem("refreshToken", refreshToken);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const logout = async () => {
    setIsUpdating(true);

    dispatch({
      type: "SET_AUTH_STATE",
      payload: {
        isLoading: false,
        isAuthenticated: false,
        isEmailVerified: false,
        otpVerifiedForSession: false,
      },
    });

    await AsyncStorage.removeItem("accessToken");
    await AsyncStorage.removeItem("refreshToken");

    try {
      const result = await logoutMutation();

      if (!result?.data?.logout?.success) {
        throw new Error("Failed to logout");
      }
      // Clear any stored returnTo URL so post-login does not redirect
      await returnToUtils.clearReturnToUrl();

      if (apolloClient) {
        await apolloClient.clearStore();
      }

      setIsUpdating(false);

      appDispatch({
        type: actions.RESET_STATE,
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const verifyOTPForSession = async (accessToken, refreshToken) => {
    try {
      await AsyncStorage.setItem("accessToken", accessToken);
      await AsyncStorage.setItem("refreshToken", refreshToken);
      setIsUpdating(true);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  if (loading || state.isLoading) {
    return null;
  }

  return (
    <AuthContext.Provider
      value={{
        isUpdating,
        isAuthenticated: state.isAuthenticated,
        isEmailVerified: state.isEmailVerified,
        otpVerifiedForSession: state.otpVerifiedForSession,
        login,
        logout,
        verifyOTPForSession,
      }}>
      {children}
    </AuthContext.Provider>
  );
};
